import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Button, CircularProgress, Alert, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardContent, Grid
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import { dcApi } from '../services/dc.api'
import { DCStoreTransferItem } from '../types/dc.types'

const statusColors: Record<string, 'default' | 'primary' | 'info' | 'success'> = {
  draft: 'default',
  confirmed: 'primary',
  exported: 'info',
  completed: 'success'
}

interface DistributorGroup {
  distributorId: number | null
  distributorName: string
  invoiceNumber: string | null
  purchaseNumber: string | null
  items: DCStoreTransferItem[]
  totalQty: number
  totalAmount: number
}

export default function DCTransferDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

  const transferId = parseInt(id!)

  const { data, isLoading, error } = useQuery({
    queryKey: ['transfer', id],
    queryFn: () => dcApi.getTransfer(transferId),
    enabled: !!id
  })

  const confirmMutation = useMutation({
    mutationFn: () => dcApi.confirmTransfer(transferId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfer', id] })
      setSnackbar({ open: true, message: 'Transfer completed successfully' })
    }
  })

  const deleteMutation = useMutation({
    mutationFn: () => dcApi.deleteTransfer(transferId),
    onSuccess: () => {
      navigate('/transfers')
    }
  })

  const handleExportCSV = async () => {
    try {
      const blob = await dcApi.exportTransferCSV(transferId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `transfer_${id}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to export CSV' })
    }
  }

  const downloadDistributorCSV = async (distributorId: number) => {
    try {
      const blob = await dcApi.exportTransferDistributorCSV(transferId, distributorId)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Let the Content-Disposition header suggest the filename, but set a fallback
      a.download = `transfer_${id}_distributor_${distributorId}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to export distributor CSV' })
    }
  }

  // Group items by distributor
  const distributorGroups = useMemo<DistributorGroup[]>(() => {
    if (!data?.items) return []
    const groups: Record<string, DistributorGroup> = {}
    for (const item of data.items) {
      const key = String(item.distributor_id ?? 'unknown')
      if (!groups[key]) {
        groups[key] = {
          distributorId: item.distributor_id ?? null,
          distributorName: item.distributor_name || 'Unknown Distributor',
          invoiceNumber: item.invoice_number || null,
          purchaseNumber: item.purchase_number || null,
          items: [],
          totalQty: 0,
          totalAmount: 0
        }
      }
      groups[key].items.push(item)
      groups[key].totalQty += item.transfer_qty
      const amt = item.line_total != null
        ? item.line_total
        : item.rate != null
          ? item.transfer_qty * item.rate
          : 0
      groups[key].totalAmount += amt
    }
    return Object.values(groups).sort((a, b) => a.distributorName.localeCompare(b.distributorName))
  }, [data?.items])

  const hasMultipleDistributors = distributorGroups.length > 1

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error || !data) {
    return <Alert severity="error">Failed to load transfer: {(error as Error)?.message || 'Not found'}</Alert>
  }

  const { transfer, items } = data
  const isDraft = transfer.status === 'draft'

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/transfers')}>
            Back
          </Button>
          <Typography variant="h5">{transfer.transfer_number}</Typography>
          <Chip
            label={transfer.status}
            size="small"
            color={statusColors[transfer.status] || 'default'}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
          >
            Export All CSV
          </Button>
          {isDraft && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={confirmMutation.isPending ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={() => confirmMutation.mutate()}
                disabled={confirmMutation.isPending}
              >
                Complete Transfer
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirm(true)}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Store</Typography>
              <Typography variant="h6">{transfer.store_name}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Date</Typography>
              <Typography variant="h6">
                {transfer.transfer_date
                  ? new Date(transfer.transfer_date).toLocaleDateString('en-IN')
                  : transfer.created_at
                    ? new Date(transfer.created_at).toLocaleDateString('en-IN')
                    : '-'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Items</Typography>
              <Typography variant="h6">{transfer.total_items}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Amount</Typography>
              <Typography variant="h6">
                Rs. {(transfer.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {transfer.notes && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Notes:</strong> {transfer.notes}
        </Alert>
      )}

      {confirmMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(confirmMutation.error as Error).message}
        </Alert>
      )}

      {/* Items grouped by distributor */}
      {items.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No items in this transfer.</Typography>
        </Paper>
      ) : hasMultipleDistributors ? (
        // Multiple distributors — show grouped sections
        distributorGroups.map((group) => (
          <Box key={group.distributorName} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box>
                <Typography variant="h6" component="span">
                  {group.distributorName}
                </Typography>
                {group.invoiceNumber && (
                  <Chip
                    label={`Bill: ${group.invoiceNumber}`}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ ml: 1 }}
                  />
                )}
                {group.purchaseNumber && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({group.purchaseNumber})
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {group.items.length} items | Rs. {group.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              {group.distributorId && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadDistributorCSV(group.distributorId!)}
                >
                  Download CSV
                </Button>
              )}
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Medicine Name</TableCell>
                    <TableCell>Pack Size</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell align="right">Transfer Qty</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">MRP</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.items.map((item: DCStoreTransferItem) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.medicine_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.pack_size || '-'}</TableCell>
                      <TableCell>{item.batch_no || '-'}</TableCell>
                      <TableCell>
                        {item.expiry_date
                          ? new Date(item.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                          : '-'}
                      </TableCell>
                      <TableCell align="right">{item.transfer_qty}</TableCell>
                      <TableCell align="right">
                        {item.rate != null
                          ? item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {item.mrp != null
                          ? item.mrp.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {item.line_total != null
                          ? `Rs. ${item.line_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                          : item.rate != null
                            ? `Rs. ${(item.transfer_qty * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                            : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" fontWeight={600}>Subtotal</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>{group.totalQty}</Typography>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        Rs. {group.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      ) : (
        // Single distributor — show flat table with distributor info
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">Transfer Items</Typography>
              {distributorGroups[0]?.distributorName && distributorGroups[0].distributorName !== 'Unknown Distributor' && (
                <Chip
                  label={distributorGroups[0].distributorName}
                  size="small"
                  variant="outlined"
                />
              )}
              {distributorGroups[0]?.invoiceNumber && (
                <Chip
                  label={`Bill: ${distributorGroups[0].invoiceNumber}`}
                  size="small"
                  variant="outlined"
                  color="info"
                />
              )}
            </Box>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Medicine Name</TableCell>
                  <TableCell>Pack Size</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell>Expiry</TableCell>
                  <TableCell align="right">Transfer Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">MRP</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item: DCStoreTransferItem) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {item.medicine_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.pack_size || '-'}</TableCell>
                    <TableCell>{item.batch_no || '-'}</TableCell>
                    <TableCell>
                      {item.expiry_date
                        ? new Date(item.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
                        : '-'}
                    </TableCell>
                    <TableCell align="right">{item.transfer_qty}</TableCell>
                    <TableCell align="right">
                      {item.rate != null
                        ? item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {item.mrp != null
                        ? item.mrp.toLocaleString('en-IN', { minimumFractionDigits: 2 })
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {item.line_total != null
                        ? `Rs. ${item.line_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                        : item.rate != null
                          ? `Rs. ${(item.transfer_qty * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                          : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Transfer?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {transfer.transfer_number}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  )
}
